import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import CTAButton from "./CTAButton";
import ScrollProgress from "./ScrollProgress";
import StickyQuoteCTA from "./StickyQuoteCTA";

const PHONE_DISPLAY = "0494 756 111";
const PHONE_TEL = "0494756111";

const NAV = [
  { label: "Menu", to: "/menu-photography" },
  { label: "Food", to: "/food-photography" },
  { label: "Venue", to: "/venue-photography" },
  { label: "Brand", to: "/brand-photography" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = ({ initialOverlay = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const overlayMode = initialOverlay && !scrolled && !mobileOpen;

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        overlayMode
          ? "bg-transparent text-bone border-b border-bone/10"
          : "bg-ink/95 backdrop-blur-md text-bone border-b border-bone/10"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10">
        <div
          className={`flex items-center justify-between gap-3 sm:gap-6 transition-all duration-500 ${
            scrolled ? "h-16" : "h-20 md:h-24"
          }`}
        >
          {/* Wordmark */}
          <Link to="/" data-testid="header-logo" className="flex items-baseline gap-3 min-w-0">
            <span className="font-display text-lg sm:text-xl md:text-2xl tracking-editorial leading-none truncate">
              Restaurant
              <span className="italic font-light text-ember"> Photographer</span>
            </span>
            <span className="hidden xl:inline text-[11px] uppercase tracking-wider2 opacity-60 mt-1 font-medium">
              Melbourne · Est. 2025
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav
            data-testid="primary-nav"
            className="hidden lg:flex items-center gap-7 xl:gap-9"
            aria-label="Primary"
          >
            {NAV.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`relative text-[11px] uppercase tracking-wider2 font-bold transition-colors ${
                    active ? "text-ember" : "text-bone/85 hover:text-ember"
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute -bottom-2 left-0 right-0 h-px bg-ember" aria-hidden />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right cluster — phone (sticky / always visible) + CTA */}
          <div className="flex items-center gap-3 sm:gap-5 shrink-0">
            {/* Phone — always visible. On mobile compresses to icon + tight number. */}
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="header-phone"
              className="group inline-flex items-center gap-2 sm:gap-2.5 transition-colors"
            >
              <span
                className={`relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-colors ${
                  overlayMode
                    ? "bg-ember/15 group-hover:bg-ember"
                    : "bg-ember group-hover:bg-ember-deep"
                }`}
                aria-hidden
              >
                <Phone
                  size={14}
                  strokeWidth={2}
                  className={`transition-colors ${overlayMode ? "text-ember group-hover:text-bone" : "text-bone"}`}
                />
                <span
                  className={`absolute inset-0 rounded-full ${overlayMode ? "bg-ember/30" : "bg-ember/40"} animate-ping opacity-75`}
                  aria-hidden
                />
              </span>
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-[10px] uppercase tracking-wider2 text-bone/65 mb-1 font-semibold">
                  Call direct
                </span>
                <span className="font-sans font-bold text-sm md:text-base tracking-tight text-bone group-hover:text-ember transition-colors">
                  {PHONE_DISPLAY}
                </span>
              </div>
              <span className="sr-only">{PHONE_DISPLAY}</span>
            </a>

            {/* CTA */}
            <a href="/get-a-quote" data-testid="header-cta" className="hidden sm:inline-flex">
              <CTAButton
                variant={overlayMode ? "ghost" : "primary"}
                size="sm"
              >
                Get a Shoot Quote
              </CTAButton>
            </a>

            {/* Mobile menu trigger */}
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-bone/30 text-bone hover:border-ember hover:text-ember transition-colors"
            >
              {mobileOpen ? <X size={16} strokeWidth={2} /> : <Menu size={16} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Sheet */}
      <div
        data-testid="mobile-nav"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          mobileOpen ? "max-h-[600px] opacity-100 border-t border-bone/10" : "max-h-0 opacity-0"
        } bg-ink/98 backdrop-blur-md`}
      >
        <nav className="px-6 py-8 space-y-1" aria-label="Mobile primary">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            const isService = ["Menu", "Food", "Venue", "Brand"].includes(item.label);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex items-center justify-between py-4 border-b border-bone/10 font-display text-2xl tracking-editorial transition-colors ${
                  active ? "text-ember" : "text-bone hover:text-ember"
                }`}
              >
                <span>{isService ? `${item.label} Photography` : item.label}</span>
                <span className="text-[10px] uppercase tracking-wider2 font-bold opacity-60">→</span>
              </Link>
            );
          })}
          <a
            href="/get-a-quote"
            onClick={() => setMobileOpen(false)}
            className="block pt-6"
            data-testid="mobile-nav-cta"
          >
            <CTAButton variant="primary" size="md" className="w-full">
              Get a Shoot Quote
            </CTAButton>
          </a>
        </nav>
      </div>

      {/* Thin scroll-progress bar at bottom edge of the header */}
      <ScrollProgress />
    </header>

    {/* Sticky floating CTA — appears post-hero, hides near enquiry section */}
    <StickyQuoteCTA href="#enquiry" />
    </>
  );
};

export default Header;
